import axios from "axios"
import axiosRetry from "axios-retry"
import accessToken from "helpers/jwt-token-access/accessToken"
import { createHmacSha256, generateStrongKey } from "common/utils"
import logger from "utils/logger"
import { shouldRedirectToForbidden } from "utils/apiForbiddenRedirect"
import { redirectOnApiForbidden } from "utils/redirectOnForbidden"
import { redirectToMaintenance, isMaintenanceResponse } from "utils/maintenanceRedirect"
import { redirectToLoginAfterSessionEnd } from "utils/sessionCleanup"
import { getRetryDelay, shouldRetry } from "utils/retryHandler"
import { API_TIMEOUT_MS } from "constants/apiTimeout"
import { getTenantPortalHostHeader } from "utils/portalHost"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1/"
const HMAC_SECRET_KEY = process.env.REACT_APP_HMAC_SECRET_KEY || ""

/**
 * Pre-configured Axios instance with:
 * - Auth token attachment (tenant context comes from JWT on the backend)
 * - HMAC request signing
 * - Accept-Language header
 * - Exponential-backoff retry (up to 3 attempts)
 * - 401/403 redirect handling
 */
const api = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT_MS,
})

// ── Request interceptor ──────────────────────────────────────────────────────

api.interceptors.request.use(
  async config => {
    // Auth token
    const token = await accessToken()
    if (token) {
      config.headers.Authorization = token
    }

    // Accept-Language
    const language = localStorage.getItem("I18N_LANGUAGE")
    config.headers["Accept-Language"] = language === "sa" ? "ar" : "en"

    const tenantId =
      localStorage.getItem("tenantId") ||
      localStorage.getItem("TENANT_ID") ||
      sessionStorage.getItem("TENANT_ID") ||
      sessionStorage.getItem("tenantId")

    if (tenantId) {
      config.headers["X-Tenant-ID"] = tenantId
    } else {
      const tenantPortalHost = getTenantPortalHostHeader()
      if (tenantPortalHost) {
        config.headers["X-Forwarded-Host"] = tenantPortalHost
      }
    }

    // HMAC signing
    const method = config.method ? config.method.toUpperCase() : null
    try {
      if (method === "PUT" || method === "PATCH" || method === "POST") {
        const requestBody = config.data || config.body || {}
        let bodyString = ""
        if (requestBody instanceof FormData) {
          const entries = []
          for (const entry of requestBody.entries()) {
            entries.push(entry[0] + "=" + entry[1])
          }
          bodyString = entries.join("&")
        } else if (typeof requestBody === "string") {
          bodyString = requestBody
        } else if (requestBody && typeof requestBody === "object") {
          bodyString = JSON.stringify(requestBody)
        }
        if (bodyString && HMAC_SECRET_KEY) {
          const hash = await createHmacSha256(HMAC_SECRET_KEY, bodyString)
          if (hash) config.headers["X-Signature"] = hash
        }
      } else if (method === "GET" || method === "DELETE") {
        const generatedKey = generateStrongKey()
        config.headers["x-st"] = generatedKey
        if (HMAC_SECRET_KEY) {
          const hash = await createHmacSha256(HMAC_SECRET_KEY, generatedKey)
          if (hash) config.headers["X-Signature"] = hash
        }
      }
    } catch (err) {
      logger.error("Error generating HMAC signature", err)
    }

    logger.debug(`[API] ${method} ${config.url}`)
    return config
  },
  error => Promise.reject(error)
)

// ── Response interceptor ─────────────────────────────────────────────────────

/**
 * Normalize an Axios error into a structured object that callers and
 * middleware can inspect consistently.
 *
 * @param {import("axios").AxiosError} error
 * @returns {object}
 */
const normalizeError = error => {
  const status = error?.response?.status
  const data = error?.response?.data
  return {
    status,
    message: data?.msg || data?.message || error?.message || "Unknown error",
    data,
  }
}

api.interceptors.response.use(
  response => response,
  error => {
    const { status } = normalizeError(error)
    const statusCode = error?.response?.data?.statusCode

    if (status === 401 || statusCode === 401) {
      redirectToLoginAfterSessionEnd("/login")
    }

    if (
      (status === 403 || statusCode === 403) &&
      shouldRedirectToForbidden(error, error?.config)
    ) {
      redirectOnApiForbidden()
    }

    if (isMaintenanceResponse(error)) {
      redirectToMaintenance(error)
      return Promise.reject(error)
    }

    logger.error(`[API] Error ${status || "Network"}`, error?.response?.data)

    // Attach normalized error for middleware/consumers
    const normalized = normalizeError(error)
    error.normalized = normalized
    return Promise.reject(error)
  }
)

// ── Retry configuration ──────────────────────────────────────────────────────

const retryAttempts = parseInt(process.env.REACT_APP_RETRY_ATTEMPTS, 10) || 3

axiosRetry(api, {
  retries: retryAttempts,
  retryDelay: getRetryDelay,
  retryCondition: shouldRetry,
})

export default api
